import { gql, useLazyQuery, useQuery } from "@apollo/client";
import {
  Dialog,
  DialogContent,
  Drawer,
  Grid,
  Icon,
  InputLabel,
  useMediaQuery,
} from "@mui/material";
import CustomPagination from "components/Pagination";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import DataTableCustom from "components/Tables/DataTableCustom";
import SoftTypography from "components/SoftTypography";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { formatMoneyPunto } from "utils/formatters";
import { SISTEMAS_NAME, TIPO_USER_ENUM } from "../../menuUniversitario/ModalPrecioMenu/config";

const VOUCHER_CLIENTS_REPORT_QUERY = gql`
  query reporteVouchersClientes(
    $fecha: Date
    $filter: VoucherClienteReporteFilter
    $page: Int
    $limit: Int
  ) {
    reporteVouchersClientes(fecha: $fecha, filter: $filter, page: $page, limit: $limit) {
      rows {
        id
        clienteNombre
        dni
        cantidadVouchers
        especieAsignado
        especieConsumido
        especiePorcentajeUso
        montoAsignado
        montoConsumido
        montoPorcentajeUso
      }
      pagination { total pages page limit }
    }
  }
`;

const VOUCHER_CLIENT_REPORT_DETAILS_QUERY = gql`
  query reporteVouchersClienteDetalle($dni: String!, $fecha: Date) {
    reporteVouchersClienteDetalle(dni: $dni, fecha: $fecha) {
      id
      desde
      hasta
      tipo
      cantidad
      estadoActual
      cliente { id appUsuario tipoUsuario }
      grupo { id nombre estado }
    }
  }
`;

const PAGE_SIZE = 10;
const DEFAULT_SEARCH_FILTERS = { nombreCliente: "", dni: "" };
const getTodayDate = () => dayjs().format("YYYY-MM-DD");

const STATUS_OPTIONS = [
  { value: "VIGENTE", label: "Vigente", color: "success" },
  { value: "PROXIMO", label: "Próximo", color: "info" },
  { value: "VENCIDO", label: "Vencido", color: "error" },
  { value: "SIN_SALDO", label: "Sin saldo", color: "warning" },
  { value: "NO_DISPONIBLE", label: "No disponible", color: "secondary" },
];

const TYPE_LABELS = {
  MONTO: "Monto",
  PRODUCTO: "Producto",
  CATEGORIA: "Categoría",
};

const normalizeEnumKey = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/\s+/g, "")
  .toLowerCase();

const TYPE_USER_LABELS = Object.entries(TIPO_USER_ENUM).reduce((labels, [key, label]) => ({
  ...labels,
  [normalizeEnumKey(key)]: label,
}), {});

const formatApplication = (appUsuario) => SISTEMAS_NAME[appUsuario] || appUsuario || "Particular";
const formatUserType = (tipoUsuario) => TYPE_USER_LABELS[normalizeEnumKey(tipoUsuario)] || tipoUsuario || "Sin tipo";

const formatValidityDate = (date) => {
  if (!date || !dayjs(date).isValid()) return "-";
  const validityDate = dayjs(date);
  return validityDate.format(validityDate.year() === dayjs().year() ? "DD/MM" : "DD/MM/YYYY");
};

const formatValidity = (desde, hasta) => `${formatValidityDate(desde)} – ${formatValidityDate(hasta)}`;
const formatUnits = (value) => Number(value || 0).toLocaleString("es-AR", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
const formatUnitsSummary = (consumed, assigned) => `${formatUnits(consumed)} / ${formatUnits(assigned)}`;
const formatVoucherAmount = (tipo, cantidad) => (
  tipo === "MONTO"
    ? `$ ${formatMoneyPunto(Number(cantidad || 0))}`
    : `${formatUnits(cantidad)} u`
);
const formatMoneySummary = (consumed, assigned) => (
  `$ ${formatMoneyPunto(Number(consumed || 0))} / $ ${formatMoneyPunto(Number(assigned || 0))}`
);
const formatUsagePercentage = (value) => `${Number(value || 0).toLocaleString("es-AR", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})}%`;
const renderUsageSummary = (summary, percentage) => (
  <SoftBox display="flex" alignItems="center" justifyContent="flex-end" gap={1.5} width="100%">
    <SoftTypography variant="caption" color="text" fontWeight="medium" sx={{ whiteSpace: "nowrap" }}>
      {summary}
    </SoftTypography>
    <SoftTypography
      variant="caption"
      color="dark"
      fontWeight="bold"
      sx={{ minWidth: "52px", textAlign: "right", whiteSpace: "nowrap" }}
    >
      ({percentage})
    </SoftTypography>
  </SoftBox>
);
const renderDetailText = (value) => (
  <SoftTypography
    variant="caption"
    color="text"
    fontWeight="medium"
    sx={{ fontSize: "0.7rem", lineHeight: 1.25 }}
  >
    {value}
  </SoftTypography>
);

const getStatusProps = (estadoActual) => (
  STATUS_OPTIONS.find((option) => option.value === estadoActual)
  || { value: estadoActual, label: estadoActual || "Sin estado", color: "secondary" }
);

function VoucherStatusBadge({ estadoActual }) {
  const status = getStatusProps(estadoActual);
  return (
    <SoftBox
      component="span"
      display="inline-flex"
      sx={{
        "& .MuiBadge-badge": {
          minWidth: "0 !important",
          padding: "3px 5px 2px !important",
          fontSize: "0.5rem !important",
          lineHeight: "1 !important",
        },
      }}
    >
      <SoftBadge
        variant="gradient"
        color={status.color}
        size="xs"
        badgeContent={status.label}
        container
      />
    </SoftBox>
  );
}

VoucherStatusBadge.propTypes = { estadoActual: PropTypes.string };
VoucherStatusBadge.defaultProps = { estadoActual: "" };

const TABLE_HEADER_STYLES = {
  backgroundColor: "#ffffff",
  borderBottom: "1px solid #e4e8ee",
  boxShadow: "none",
  "& .MuiTypography-root": {
    fontWeight: 700,
    color: "#64748b",
    fontSize: "0.625rem",
    letterSpacing: "0.025em",
    lineHeight: 1.3,
  },
};

function VouchersAsignadosDialog({ open, onClose }) {
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const tableRegionRef = useRef(null);
  const [filters, setFilters] = useState(DEFAULT_SEARCH_FILTERS);
  const [debouncedName, setDebouncedName] = useState("");
  const [debouncedDni, setDebouncedDni] = useState("");
  const [reportDate, setReportDate] = useState(getTodayDate);
  const [page, setPage] = useState(1);
  const [selectedClient, setSelectedClient] = useState(null);
  const validReportDate = Boolean(reportDate && dayjs(reportDate).isValid());

  useEffect(() => {
    if (!open) return undefined;
    const timeoutId = setTimeout(() => {
      setDebouncedName(filters.nombreCliente.trim());
      setDebouncedDni(filters.dni.trim());
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [filters.dni, filters.nombreCliente, open]);

  useEffect(() => {
    if (open) return;
    setFilters(DEFAULT_SEARCH_FILTERS);
    setDebouncedName("");
    setDebouncedDni("");
    setReportDate(getTodayDate());
    setPage(1);
    setSelectedClient(null);
  }, [open]);

  useEffect(() => {
    setSelectedClient(null);
  }, [debouncedDni, debouncedName, page, reportDate]);

  const queryFilter = useMemo(() => ({
    ...(debouncedName ? { nombreCliente: debouncedName } : {}),
    ...(debouncedDni ? { dni: debouncedDni } : {}),
  }), [debouncedDni, debouncedName]);

  const { data, loading, error, refetch } = useQuery(VOUCHER_CLIENTS_REPORT_QUERY, {
    variables: { fecha: reportDate, filter: queryFilter, page, limit: PAGE_SIZE },
    skip: !open || !validReportDate,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  const [loadClientDetails, {
    data: clientDetailsData,
    loading: clientDetailsLoading,
    error: clientDetailsError,
  }] = useLazyQuery(VOUCHER_CLIENT_REPORT_DETAILS_QUERY, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const report = data?.reporteVouchersClientes;
  const pagination = report?.pagination || { total: 0, pages: 0, page: 1, limit: PAGE_SIZE };
  const rows = useMemo(() => (validReportDate ? (report?.rows || []).map((row) => ({
    ...row,
    id: String(row.id),
    cantidad: Number(row.cantidadVouchers || 0),
    especie: formatUnitsSummary(row.especieConsumido, row.especieAsignado),
    especiePorcentaje: formatUsagePercentage(row.especiePorcentajeUso),
    monto: formatMoneySummary(row.montoConsumido, row.montoAsignado),
    montoPorcentaje: formatUsagePercentage(row.montoPorcentajeUso),
  })) : []), [report?.rows, validReportDate]);
  const detailRows = useMemo(() => (
    clientDetailsData?.reporteVouchersClienteDetalle || []
  ).map((voucher) => ({
    id: Number(voucher.id),
    app: formatApplication(voucher?.cliente?.appUsuario),
    tipoUsuario: formatUserType(voucher?.cliente?.tipoUsuario),
    grupo: voucher?.grupo?.nombre || "Sin grupo",
    tipo: TYPE_LABELS[voucher.tipo] || voucher.tipo || "-",
    cantidad: formatVoucherAmount(voucher.tipo, voucher.cantidad),
    vigencia: formatValidity(voucher.desde, voucher.hasta),
    estado: voucher.estadoActual || "",
    estadoValue: voucher.estadoActual,
  })), [clientDetailsData?.reporteVouchersClienteDetalle]);

  const mainColumns = useMemo(() => [
    {
      field: "clienteNombre",
      headerName: "Cliente",
      sortable: true,
      width: "220px",
      grow: 1,
      sticky: "left",
      paddingLeft: "28px",
      renderCell: (item) => <SoftTypography variant="caption" color="text" fontWeight="medium">{item.clienteNombre}</SoftTypography>,
    },
    {
      field: "dni",
      headerName: "DNI",
      sortable: true,
      width: "120px",
      renderCell: (item) => <SoftTypography variant="caption" color="text" fontWeight="medium">{item.dni}</SoftTypography>,
    },
    {
      field: "cantidad",
      headerName: "Cant.",
      sortable: true,
      align: "center",
      width: "105px",
      renderCell: (item) => <SoftTypography variant="caption" color="text" fontWeight="bold">{item.cantidad}</SoftTypography>,
    },
    {
      field: "especie",
      headerName: "Especie (consumido / asignado)",
      sortable: false,
      align: "right",
      width: "260px",
      renderCell: (item) => renderUsageSummary(item.especie, item.especiePorcentaje),
    },
    {
      field: "monto",
      headerName: "Monto (consumido / asignado)",
      sortable: false,
      align: "right",
      width: "275px",
      renderCell: (item) => renderUsageSummary(item.monto, item.montoPorcentaje),
    },
  ], []);

  const detailColumns = useMemo(() => [
    { field: "app", headerName: "APP", sortable: true, width: "105px", renderCell: (item) => renderDetailText(item.app) },
    { field: "tipoUsuario", headerName: "Tipo usuario", sortable: true, width: "135px", renderCell: (item) => renderDetailText(item.tipoUsuario) },
    { field: "grupo", headerName: "Grupo", sortable: true, width: "180px", grow: 1, renderCell: (item) => renderDetailText(item.grupo) },
    { field: "tipo", headerName: "Tipo", sortable: true, width: "105px", renderCell: (item) => renderDetailText(item.tipo) },
    { field: "cantidad", headerName: "Cantidad", sortable: true, align: "right", width: "115px", renderCell: (item) => renderDetailText(item.cantidad) },
    { field: "vigencia", headerName: "Vigencia", sortable: false, align: "center", width: "165px", renderCell: (item) => renderDetailText(item.vigencia) },
    {
      field: "estado",
      headerName: "Estado",
      sortable: false,
      align: "center",
      width: "110px",
      renderCell: (item) => <VoucherStatusBadge estadoActual={item.estadoValue} />,
    },
  ], []);

  const updateFilter = (field, value) => {
    setFilters((previous) => ({ ...previous, [field]: value }));
    setPage(1);
    setSelectedClient(null);
  };

  const updateReportDate = (value) => {
    setReportDate(value);
    setPage(1);
    setSelectedClient(null);
  };

  const clearFilters = () => {
    setFilters(DEFAULT_SEARCH_FILTERS);
    setDebouncedName("");
    setDebouncedDni("");
    setReportDate(getTodayDate());
    setPage(1);
    setSelectedClient(null);
  };

  const closeClientDrawer = () => {
    setSelectedClient(null);
    setTimeout(() => tableRegionRef.current?.focus(), 0);
  };

  const openClientDrawer = (row) => {
    setSelectedClient(row);
    loadClientDetails({ variables: { dni: row.dni, fecha: reportDate } });
  };

  const retryClientDetails = () => {
    if (!selectedClient) return;
    loadClientDetails({ variables: { dni: selectedClient.dni, fecha: reportDate } });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={false}
      aria-labelledby="vouchers-asignados-dialog-title"
      BackdropProps={{ sx: { backgroundColor: "rgba(71, 80, 93, 0.56)", backdropFilter: "blur(2px)" } }}
      PaperProps={{
        sx: {
          width: fullScreen ? "100%" : "calc(100% - 40px)",
          height: fullScreen ? "100%" : "84vh",
          maxWidth: fullScreen ? "none" : "1180px",
          maxHeight: fullScreen ? "none" : "760px",
          m: fullScreen ? 0 : 2.5,
          borderRadius: fullScreen ? 0 : "14px",
          backgroundImage: "none",
          boxShadow: "0 18px 36px rgba(15, 23, 42, 0.24)",
          overflow: "hidden",
        },
      }}
    >
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          p: { xs: 2, sm: 2.5 },
          "&:first-of-type": { pt: { xs: 2, sm: 2.5 } },
        }}
      >
        <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <SoftBox>
            <SoftTypography id="vouchers-asignados-dialog-title" component="h2" variant="h4" fontWeight="bold" sx={{ color: "#0f1d3d", fontSize: { xs: "1.35rem", sm: "1.55rem" } }}>
              Clientes con vouchers
            </SoftTypography>
            <SoftTypography variant="body2" sx={{ mt: 0.45, color: "#5f6f8d", fontSize: "0.82rem" }}>
              Vouchers utilizables por cliente y consumo acumulado a la fecha seleccionada.
            </SoftTypography>
          </SoftBox>
          <SoftButton
            color="secondary"
            circular
            iconOnly
            onClick={onClose}
            size="small"
            aria-label="Cerrar clientes con vouchers"
          >
            <Icon>close</Icon>
          </SoftButton>
        </SoftBox>

        <SoftBox mt={2} p={{ xs: 1.5, sm: 2 }} sx={{ border: "1px solid #e0e6ef", borderRadius: "12px", backgroundColor: "#f8fafc" }}>
          <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <SoftTypography variant="button" fontWeight="bold" color="dark">Consulta</SoftTypography>
            <SoftButton variant="text" color="uproPink" size="small" onClick={clearFilters} sx={{ minWidth: "auto", px: 1, cursor: "pointer" }}>
              <Icon sx={{ fontSize: "1rem !important" }}>restart_alt</Icon>&nbsp;Restablecer
            </SoftButton>
          </SoftBox>
          <Grid container spacing={1.25} alignItems="flex-start">
            <Grid item xs={12} sm={6} lg={4}>
              <InputLabel htmlFor="voucher-report-name"><SoftTypography variant="caption" fontWeight="bold">Nombre del cliente</SoftTypography></InputLabel>
              <SoftInput id="voucher-report-name" value={filters.nombreCliente} placeholder="Nombre o apellido" onChange={(event) => updateFilter("nombreCliente", event.target.value)} icon={{ component: "search", direction: "left" }} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <InputLabel htmlFor="voucher-report-dni"><SoftTypography variant="caption" fontWeight="bold">DNI</SoftTypography></InputLabel>
              <SoftInput id="voucher-report-dni" value={filters.dni} placeholder="Número" onChange={(event) => updateFilter("dni", event.target.value)} />
            </Grid>
            <Grid item xs={12} sm={12} lg={5}>
              <InputLabel htmlFor="voucher-report-date"><SoftTypography variant="caption" fontWeight="bold">Fecha de corte</SoftTypography></InputLabel>
              <SoftInput id="voucher-report-date" type="date" value={reportDate} onChange={(event) => updateReportDate(event.target.value)} error={!validReportDate} />
              <SoftTypography variant="caption" color={validReportDate ? "secondary" : "error"} display="block" mt={0.5} role={!validReportDate ? "alert" : undefined}>
                {!validReportDate && "Seleccione una fecha válida."}
              </SoftTypography>
            </Grid>
          </Grid>
        </SoftBox>

        <SoftBox mt={1.5} display="flex" flexDirection="column" flex={1} minHeight={0}>
          {error ? (
            <SoftBox minHeight="220px" display="grid" p={2} sx={{ placeItems: "center", border: "1px solid #f1c7c7", borderRadius: "12px", backgroundColor: "#fffafa" }} role="alert">
              <SoftBox textAlign="center">
                <SoftTypography variant="button" color="error" fontWeight="bold" display="block">No se pudo cargar el reporte de vouchers.</SoftTypography>
                <SoftButton mt={1.5} variant="outlined" color="error" size="small" onClick={() => refetch()}>
                  Reintentar
                </SoftButton>
              </SoftBox>
            </SoftBox>
          ) : (
            <SoftBox
              ref={tableRegionRef}
              tabIndex={-1}
              height="100%"
              minHeight={0}
              sx={{
                position: "relative",
                border: "1px solid #dfe5ed",
                borderRadius: "12px",
                overflow: "hidden",
                backgroundColor: "#fff",
                "&:focus-visible": { outline: "2px solid #3463ce", outlineOffset: "2px" },
              }}
            >
              <DataTableCustom
                columns={mainColumns}
                data={rows}
                loading={loading}
                loadingMessage="Cargando reporte de clientes..."
                emptyMessage={validReportDate ? "No se encontraron clientes para la consulta" : "Seleccione una fecha de corte"}
                showActions={false}
                showFooter={false}
                sortable
                defaultSort={{ field: "clienteNombre", direction: "asc" }}
                autoCalculateWidths={false}
                maxHeight="100%"
                zebraStripes={false}
                rowPaddingY={0.65}
                headerStyles={TABLE_HEADER_STYLES}
                customRowStyles={() => ({
                  backgroundColor: "#ffffff",
                  "&:hover": { backgroundColor: "#f3f6fa" },
                  "&:focus-visible": { outline: "2px solid #3463ce", outlineOffset: "-2px" },
                })}
                onRowClick={openClientDrawer}
                showVerticalBorders={false}
                styleCard={{ height: "100%", border: "none", boxShadow: "none" }}
              />
              <Drawer
                anchor="right"
                open={Boolean(selectedClient)}
                variant="persistent"
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  zIndex: 10,
                  pointerEvents: selectedClient ? "auto" : "none",
                  "& .MuiDrawer-paper": {
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    boxSizing: "border-box",
                    border: "none",
                    backgroundColor: "#fff",
                    overflow: "hidden",
                  },
                }}
              >
                {selectedClient ? (
                  <SoftBox display="flex" flexDirection="column" height="100%" minHeight={0} p={{ xs: 1.5, sm: 2 }}>
                    <SoftBox display="flex" alignItems="center" gap={1.25} sx={{ flex: "0 0 auto" }}>
                      <SoftButton
                        variant="outlined"
                        color="primary"
                        size="small"
                        circular
                        iconOnly
                        autoFocus
                        onClick={closeClientDrawer}
                        aria-label="Volver al listado de clientes"
                        sx={{ cursor: "pointer", "&:focus-visible": { outline: "2px solid #3463ce", outlineOffset: "2px" } }}
                      >
                        <Icon>arrow_back</Icon>
                      </SoftButton>
                      <SoftBox minWidth={0}>
                        <SoftTypography variant="button" color="dark" fontWeight="bold" display="block">
                          {selectedClient.clienteNombre}
                        </SoftTypography>
                        <SoftTypography variant="caption" color="secondary" display="block">
                          DNI {selectedClient.dni} · {selectedClient.cantidad} {selectedClient.cantidad === 1 ? "voucher vigente" : "vouchers vigentes"} al {dayjs(reportDate).format("DD/MM/YYYY")}
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                    <SoftBox mt={1.5} flex={1} minHeight={0}>
                      {clientDetailsError ? (
                        <SoftBox height="100%" minHeight="180px" display="grid" sx={{ placeItems: "center", border: "1px solid #f1c7c7", borderRadius: "10px", backgroundColor: "#fffafa" }} role="alert">
                          <SoftBox textAlign="center" p={2}>
                            <SoftTypography variant="button" color="error" fontWeight="bold" display="block">
                              No se pudieron cargar los vouchers del cliente.
                            </SoftTypography>
                            <SoftButton mt={1.5} variant="outlined" color="error" size="small" onClick={retryClientDetails}>
                              Reintentar
                            </SoftButton>
                          </SoftBox>
                        </SoftBox>
                      ) : (
                        <DataTableCustom
                          columns={detailColumns}
                          data={detailRows}
                          loading={clientDetailsLoading}
                          loadingMessage="Consultando vouchers del cliente..."
                          emptyMessage="El cliente no tiene vouchers vigentes en esta fecha"
                          showActions={false}
                          showFooter={false}
                          sortable
                          defaultSort={{ field: "grupo", direction: "asc" }}
                          autoCalculateWidths={false}
                          maxHeight="100%"
                          zebraStripes={false}
                          rowPaddingY={0.5}
                          headerStyles={TABLE_HEADER_STYLES}
                          showVerticalBorders={false}
                          styleCard={{ height: "100%", border: "1px solid #e4e8ee", boxShadow: "none" }}
                        />
                      )}
                    </SoftBox>
                  </SoftBox>
                ) : null}
              </Drawer>
            </SoftBox>
          )}
        </SoftBox>

        {!selectedClient && validReportDate && !error && (loading || Number(pagination.total || 0) > 0) ? (
          <SoftBox mt={1} sx={{ flex: "0 0 auto" }}>
            <CustomPagination
              loading={loading}
              length={rows.length}
              total={Number(pagination.total || 0)}
              pages={Number(pagination.pages || 0)}
              page={page}
              setPage={setPage}
              noCard
            />
          </SoftBox>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

VouchersAsignadosDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default VouchersAsignadosDialog;
